import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, ChevronDown, X } from "lucide-react";
import LANGUAGES from "../data/languages.json";
import COUNTRIES from "../data/countries.json";

// ---------------------- LanguageMultiSelect ----------------------
export function LanguageMultiSelect({ value = [], onChange, placeholder, dataTestid = "language-multiselect" }) {
  const { t } = useTranslation("common");
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const selectedSet = useMemo(() => new Set((value || []).map((v) => v.toLowerCase())), [value]);
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return LANGUAGES.filter((l) => !needle || l.label.toLowerCase().includes(needle) || l.native.toLowerCase().includes(needle) || l.code.includes(needle));
  }, [q]);

  const toggle = (code) => {
    const lc = code.toLowerCase();
    if (selectedSet.has(lc)) {
      onChange((value || []).filter((v) => v.toLowerCase() !== lc));
    } else {
      onChange([...(value || []), lc]);
    }
  };

  const removeChip = (code) => onChange((value || []).filter((v) => v.toLowerCase() !== code.toLowerCase()));

  return (
    <div className="relative" data-testid={dataTestid}>
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2 text-left text-sm"
        data-testid={`${dataTestid}-button`}>
        <div className="flex flex-wrap gap-1">
          {(value || []).length === 0 && <span className="text-slate-400">{placeholder || t("languageMultiPlaceholder", "Choose languages")}</span>}
          {(value || []).map((code) => {
            const lang = LANGUAGES.find((l) => l.code === code.toLowerCase()) || { native: code, code };
            return (
              <span key={code} className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-xs text-indigo-700" data-testid={`${dataTestid}-chip-${code}`}>
                {lang.native} <span className="text-slate-400">({code})</span>
                <button type="button" onClick={(e) => { e.stopPropagation(); removeChip(code); }} className="rounded-full hover:bg-indigo-100" data-testid={`${dataTestid}-chip-remove-${code}`}>
                  <X className="h-3 w-3" />
                </button>
              </span>
            );
          })}
        </div>
        <ChevronDown className="h-4 w-4 text-slate-400" />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 max-h-72 w-full overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg">
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search", "Search…")}
            className="sticky top-0 z-10 w-full border-b border-slate-100 bg-white px-3 py-2 text-sm outline-none"
            data-testid={`${dataTestid}-search`} />
          {filtered.map((l) => {
            const checked = selectedSet.has(l.code);
            return (
              <button key={l.code} type="button" onClick={() => toggle(l.code)}
                className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-slate-50"
                data-testid={`${dataTestid}-option-${l.code}`}>
                <span className="text-slate-800">{l.native} <span className="text-slate-400">({l.code})</span></span>
                {checked && <Check className="h-4 w-4 text-indigo-600" />}
              </button>
            );
          })}
          {filtered.length === 0 && <div className="px-3 py-4 text-center text-xs text-slate-400">{t("noResults", "No results")}</div>}
        </div>
      )}
    </div>
  );
}

// ---------------------- CountryMultiSelect ----------------------
export function CountryMultiSelect({ value = [], onChange, placeholder, dataTestid = "country-multiselect" }) {
  const { i18n, t } = useTranslation("common");
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const lang = (i18n.resolvedLanguage || "en").slice(0, 2);
  const labelOf = (c) => c[lang] || c.en;
  const upperSet = useMemo(() => new Set((value || []).map((v) => v.toUpperCase())), [value]);
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return COUNTRIES.filter((c) => !needle || labelOf(c).toLowerCase().includes(needle) || c.en.toLowerCase().includes(needle) || c.code.toLowerCase().includes(needle));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, lang]);

  const toggle = (code) => {
    const uc = code.toUpperCase();
    if (upperSet.has(uc)) onChange((value || []).filter((v) => v.toUpperCase() !== uc));
    else onChange([...(value || []), uc]);
  };
  const removeChip = (code) => onChange((value || []).filter((v) => v.toUpperCase() !== code.toUpperCase()));

  return (
    <div className="relative" data-testid={dataTestid}>
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2 text-left text-sm"
        data-testid={`${dataTestid}-button`}>
        <div className="flex flex-wrap gap-1">
          {(value || []).length === 0 && <span className="text-slate-400">{placeholder || t("countryMultiPlaceholder", "Choose countries")}</span>}
          {(value || []).map((code) => {
            const c = COUNTRIES.find((x) => x.code === code.toUpperCase()) || { code, en: code };
            return (
              <span key={code} className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-xs text-indigo-700" data-testid={`${dataTestid}-chip-${code}`}>
                {labelOf(c)} <span className="text-slate-400">({code.toUpperCase()})</span>
                <button type="button" onClick={(e) => { e.stopPropagation(); removeChip(code); }} className="rounded-full hover:bg-indigo-100" data-testid={`${dataTestid}-chip-remove-${code}`}>
                  <X className="h-3 w-3" />
                </button>
              </span>
            );
          })}
        </div>
        <ChevronDown className="h-4 w-4 text-slate-400" />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 max-h-72 w-full overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg">
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search", "Search…")}
            className="sticky top-0 z-10 w-full border-b border-slate-100 bg-white px-3 py-2 text-sm outline-none"
            data-testid={`${dataTestid}-search`} />
          {filtered.map((c) => {
            const checked = upperSet.has(c.code);
            return (
              <button key={c.code} type="button" onClick={() => toggle(c.code)}
                className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-slate-50"
                data-testid={`${dataTestid}-option-${c.code}`}>
                <span className="text-slate-800">{labelOf(c)} <span className="text-slate-400">({c.code})</span></span>
                {checked && <Check className="h-4 w-4 text-indigo-600" />}
              </button>
            );
          })}
          {filtered.length === 0 && <div className="px-3 py-4 text-center text-xs text-slate-400">{t("noResults", "No results")}</div>}
        </div>
      )}
    </div>
  );
}

// ---------------------- LanguageSelect (single) ----------------------
export function LanguageSelect({ value, onChange, placeholder, dataTestid = "language-select" }) {
  const { t } = useTranslation("common");
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const selected = LANGUAGES.find((l) => l.code === (value || "").toLowerCase());
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return LANGUAGES.filter((l) => !needle || l.label.toLowerCase().includes(needle) || l.native.toLowerCase().includes(needle) || l.code.includes(needle));
  }, [q]);
  return (
    <div className="relative" data-testid={dataTestid}>
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2 text-left text-sm"
        data-testid={`${dataTestid}-button`}>
        {selected ? <span className="text-slate-800">{selected.native} <span className="text-slate-400">({selected.code})</span></span> : <span className="text-slate-400">{placeholder || t("languagePlaceholder", "Choose language")}</span>}
        <ChevronDown className="h-4 w-4 text-slate-400" />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 max-h-72 w-full overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg">
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search", "Search…")}
            className="sticky top-0 z-10 w-full border-b border-slate-100 bg-white px-3 py-2 text-sm outline-none"
            data-testid={`${dataTestid}-search`} />
          {filtered.map((l) => (
            <button key={l.code} type="button" onClick={() => { onChange(l.code); setOpen(false); setQ(""); }}
              className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-slate-50"
              data-testid={`${dataTestid}-option-${l.code}`}>
              <span className="text-slate-800">{l.native} <span className="text-slate-400">({l.code})</span></span>
              {value?.toLowerCase() === l.code && <Check className="h-4 w-4 text-indigo-600" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------- CountrySelect (single) ----------------------
export function CountrySelect({ value, onChange, placeholder, dataTestid = "country-select", allowClear = true }) {
  const { i18n, t } = useTranslation("common");
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const lang = (i18n.resolvedLanguage || "en").slice(0, 2);
  const labelOf = (c) => c[lang] || c.en;
  const selected = COUNTRIES.find((c) => c.code === (value || "").toUpperCase());
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return COUNTRIES.filter((c) => !needle || labelOf(c).toLowerCase().includes(needle) || c.en.toLowerCase().includes(needle) || c.code.toLowerCase().includes(needle));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, lang]);
  return (
    <div className="relative" data-testid={dataTestid}>
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2 text-left text-sm"
        data-testid={`${dataTestid}-button`}>
        {selected ? <span className="text-slate-800">{labelOf(selected)} <span className="text-slate-400">({selected.code})</span></span> : <span className="text-slate-400">{placeholder || t("countryPlaceholder", "Choose country")}</span>}
        <ChevronDown className="h-4 w-4 text-slate-400" />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 max-h-72 w-full overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg">
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search", "Search…")}
            className="sticky top-0 z-10 w-full border-b border-slate-100 bg-white px-3 py-2 text-sm outline-none"
            data-testid={`${dataTestid}-search`} />
          {allowClear && (
            <button type="button" onClick={() => { onChange(""); setOpen(false); setQ(""); }}
              className="block w-full border-b border-slate-100 px-3 py-2 text-left text-sm text-slate-500 hover:bg-slate-50"
              data-testid={`${dataTestid}-clear`}>{t("none", "—")}</button>
          )}
          {filtered.map((c) => (
            <button key={c.code} type="button" onClick={() => { onChange(c.code); setOpen(false); setQ(""); }}
              className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-slate-50"
              data-testid={`${dataTestid}-option-${c.code}`}>
              <span className="text-slate-800">{labelOf(c)} <span className="text-slate-400">({c.code})</span></span>
              {value?.toUpperCase() === c.code && <Check className="h-4 w-4 text-indigo-600" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
